import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BadgeCheck, Loader2, XCircle } from "lucide-react";
import api from "../api";
import { useAuth } from "../context/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refreshUser } = useAuth();
  const [state, setState] = useState("polling");

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (!cancelled) setState("paid");
          await refreshUser();
          return;
        }
      } catch {
        // keep polling briefly
      }
      attempts += 1;
      if (attempts >= 15) {
        if (!cancelled) setState("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, refreshUser]);

  return (
    <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="payment-success-page">
      <div className="card-surface p-10">
        {state === "polling" && (
          <>
            <Loader2 size={32} className="mx-auto animate-spin text-[#C58342]" />
            <h1 className="font-display text-2xl text-stone-900 mt-5">Confirming your payment…</h1>
            <p className="text-sm text-stone-500 mt-2">This takes a few seconds.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <span className="mx-auto w-14 h-14 rounded-full bg-[#ECFDF5] border border-[#A7F3D0] flex items-center justify-center text-[#065F46]">
              <BadgeCheck size={26} />
            </span>
            <h1 className="font-display text-2xl text-stone-900 mt-5" data-testid="payment-confirmed-heading">
              Welcome to the club
            </h1>
            <p className="text-sm text-stone-600 mt-3">
              Your membership is active. Your profile now shows your member ribbon.
            </p>
            <Link to="/discover" className="btn-primary mt-7" data-testid="payment-success-discover-btn">
              Start discovering
            </Link>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <XCircle size={32} className="mx-auto text-stone-400" />
            <h1 className="font-display text-2xl text-stone-900 mt-5">We're still confirming</h1>
            <p className="text-sm text-stone-600 mt-3">
              Your payment may take a moment to settle. Check your membership status shortly.
            </p>
            <Link to="/pricing" className="btn-secondary mt-7" data-testid="payment-back-pricing-btn">
              Back to membership
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="payment-cancel-page">
      <div className="card-surface p-10">
        <XCircle size={32} className="mx-auto text-stone-400" />
        <h1 className="font-display text-2xl text-stone-900 mt-5">Checkout cancelled</h1>
        <p className="text-sm text-stone-600 mt-3">No charge was made. You can subscribe whenever you're ready.</p>
        <Link to="/pricing" className="btn-primary mt-7" data-testid="payment-cancel-back-btn">
          View membership plans
        </Link>
      </div>
    </div>
  );
}
